import type { EventView, PayloadView, ShipmentDetail } from '@/lib/api/shipments';
import { anyDate } from '@/lib/format/carrier-time';

// S03 · Timeline (07 §3): the rows of the full timeline, built from
// GET /internal/shipments/:id/events?include=withdrawn plus the Plancia
// estimate of the detail answer, and the pure helpers the tab uses (filters,
// counts, grouping by day, «sent to Dokless in rev N», exports).

export type RowKind = 'fact' | 'forecast' | 'estimate';
export type KindFilter = 'all' | 'facts' | 'forecasts' | 'withdrawn';
export const KIND_FILTERS: readonly KindFilter[] = ['all', 'facts', 'forecasts', 'withdrawn'];

/** The Plancia estimate's pseudo-source (the API's event sources are carrier / manual / …). */
export const ESTIMATE_SOURCE = 'plancia';

export interface TimelineRow {
  id: string;
  kind: RowKind;
  code: string;
  /** When it happened (or is expected); null when the carrier gave no date. */
  at: Date | null;
  /** The time as the API wrote it (a carrier time as written, ADR 0055), for the exports. */
  atRaw?: string | null;
  dateOnly: boolean;
  container: string | null;
  /** «UNLOCODE Name» when the place is resolved, else the carrier's raw text. */
  place: string | null;
  /** The carrier's original text (shown in quotes and on hover). */
  originalText: string | null;
  vessel: string | null;
  voyage: string | null;
  source: string;
  observedBy: string | null;
  readId: string | null;
  /** When Plancia recorded it: the read that brought it. */
  readAt: Date | null;
  ais: boolean;
  /** The first delivered revision built after the row was recorded (inferred, see `revisionFor`). */
  revision: number | null;
  /** Estimate only: the basis (route, ais…). */
  basis: string | null;
}

export interface WithdrawnRow {
  id: string;
  code: string;
  at: Date | null;
  atRaw?: string | null;
  dateOnly: boolean;
  place: string | null;
  container: string | null;
  reason: string;
}

export interface WithdrawnGroup {
  reason: string;
  rows: WithdrawnRow[];
  /** previous_life: the old voyage, oldest place → newest place, and its month. */
  from: string | null;
  to: string | null;
  month: Date | null;
}

export interface DayGroup {
  key: string;
  date: Date;
  future: boolean;
  rows: TimelineRow[];
}

const UNLOCODE = /^[A-Z]{2}[A-Z0-9]{3}$/;

type Point = { location_id: string | null; unlocode: string | null; name: string | null };

/** Places the shipment already knows (origin, destination, legs), by location id and by UN/LOCODE. */
export function placeBook(shipment: Pick<ShipmentDetail, 'origin_point' | 'destination_point' | 'legs'>) {
  const byId = new Map<string, string>();
  const byCode = new Map<string, string>();
  const points: Point[] = [
    shipment.origin_point,
    shipment.destination_point,
    ...shipment.legs.flatMap((l) => [l.from, l.to]),
  ];
  for (const p of points) {
    if (!p.unlocode) continue;
    const label = [p.unlocode, p.name].filter(Boolean).join(' ');
    if (p.location_id) byId.set(p.location_id, label);
    byCode.set(p.unlocode, label);
  }
  return {
    resolve(locationId: string | null, raw: string | null): string | null {
      if (locationId && byId.has(locationId)) return byId.get(locationId) ?? null;
      const code = raw?.trim().toUpperCase();
      if (code && UNLOCODE.test(code) && byCode.has(code)) return byCode.get(code) ?? null;
      return raw;
    },
  };
}

/** «ITGOA Genova» → «Genova». */
export const placeName = (place: string | null) =>
  (place ?? '').replace(/^[A-Z]{2}[A-Z0-9]{3}\s+/, '') || null;

const hasAis = (confirmedBy: unknown) =>
  Array.isArray(confirmedBy) &&
  confirmedBy.some(
    (c) =>
      c === 'ais' || (typeof c === 'object' && c !== null && (c as { source?: unknown }).source === 'ais'),
  );

/** A carrier time as written, a Plancia instant in local time (ADR 0055). */
const date = (value: string | null | undefined) => anyDate(value);

/**
 * «Sent to Dokless in rev N»: the API does not link events to payloads, so
 * the revision is inferred — the first full payload Dokless accepted that was
 * built at or after the moment Plancia recorded the row.
 */
export function revisionFor(
  recordedAt: string | null | undefined,
  payloads: readonly PayloadView[],
): number | null {
  if (!recordedAt) return null;
  const t = Date.parse(recordedAt);
  if (!Number.isFinite(t)) return null;
  let best: PayloadView | null = null;
  for (const p of payloads) {
    if (p.state !== 'delivered' || p.kind === 'position') continue;
    if (Date.parse(p.created_at) < t) continue;
    if (!best || p.revision < best.revision) best = p;
  }
  return best?.revision ?? null;
}

interface Estimate {
  at: string;
  basis: string | null;
  port: string | null;
  computed_at: string | null;
}

function estimateOf(shipment: ShipmentDetail): Estimate | null {
  if (shipment.status === 'arrived' || shipment.status === 'closed') return null;
  const e = shipment.latest_estimate as Record<string, unknown> | null | undefined;
  const str = (v: unknown) => (typeof v === 'string' && v ? v : null);
  if (e && typeof e === 'object' && str(e.at))
    return {
      at: str(e.at) as string,
      basis: str(e.basis),
      port: str(e.port),
      computed_at: str(e.computed_at),
    };
  return shipment.plancia_eta
    ? { at: shipment.plancia_eta, basis: null, port: null, computed_at: null }
    : null;
}

export function toRow(
  e: EventView,
  book: ReturnType<typeof placeBook>,
  payloads: readonly PayloadView[],
): TimelineRow {
  return {
    id: e.id,
    kind: e.status === 'planned' ? 'forecast' : 'fact',
    code: e.code,
    at: date(e.occurred_at),
    atRaw: e.occurred_at,
    dateOnly: e.date_only,
    container: e.container,
    // The API resolves the place (UN/LOCODE + name) and says the read's time and the revision when it can.
    place: e.place
      ? [e.place.unlocode, e.place.name].filter(Boolean).join(' ')
      : book.resolve(e.location_id, e.location),
    originalText: e.original_text,
    vessel: e.vessel,
    voyage: e.voyage,
    source: e.source,
    observedBy: e.observed_by,
    readId: e.read_id,
    readAt: date(e.read_at ?? e.created_at),
    ais: hasAis(e.confirmed_by),
    revision: e.dokless_revision ?? revisionFor(e.created_at, payloads),
    basis: null,
  };
}

/** Every row of the timeline, newest first: the API's events and the Plancia estimate. */
export function buildRows(
  shipment: ShipmentDetail,
  events: readonly EventView[],
  payloads: readonly PayloadView[],
): TimelineRow[] {
  const book = placeBook(shipment);
  const rows = events.map((e) => toRow(e, book, payloads));
  const est = estimateOf(shipment);
  if (est) {
    const destination = shipment.destination_point;
    const current = shipment.legs.find((l) => l.current) ?? shipment.legs.at(-1);
    rows.push({
      id: 'plancia-estimate',
      kind: 'estimate',
      code: 'vessel_arrived',
      at: date(est.at),
      dateOnly: false,
      container: null,
      place: book.resolve(null, est.port) ?? book.resolve(destination.location_id, destination.unlocode),
      originalText: null,
      vessel: shipment.conveyance_detail?.name ?? current?.vessel ?? shipment.booked_vessel,
      voyage: current?.voyage ?? shipment.booked_voyage,
      source: ESTIMATE_SOURCE,
      observedBy: null,
      readId: null,
      readAt: date(est.computed_at),
      ais: false,
      revision: est.computed_at
        ? revisionFor(est.computed_at, payloads)
        : (shipment.revision.last_delivered ?? null),
      basis: est.basis,
    });
  }
  return sortRows(rows);
}

export function sortRows(rows: TimelineRow[]): TimelineRow[] {
  const ms = (r: TimelineRow) => r.at?.getTime() ?? Number.NEGATIVE_INFINITY;
  return [...rows].sort((a, b) => ms(b) - ms(a));
}

export function withdrawnRows(
  events: readonly EventView[],
  book: ReturnType<typeof placeBook>,
): WithdrawnRow[] {
  return events
    .map((e) => ({
      id: e.id,
      code: e.code,
      at: date(e.occurred_at),
      atRaw: e.occurred_at,
      dateOnly: e.date_only,
      place: book.resolve(e.location_id, e.location),
      container: e.container,
      reason: e.withdrawn_reason ?? 'manual',
    }))
    .sort((a, b) => (b.at?.getTime() ?? 0) - (a.at?.getTime() ?? 0));
}

/** Withdrawn rows by reason, in the order the reasons first appear (newest first inside). */
export function groupWithdrawn(rows: readonly WithdrawnRow[]): WithdrawnGroup[] {
  const groups = new Map<string, WithdrawnRow[]>();
  for (const r of rows) groups.set(r.reason, [...(groups.get(r.reason) ?? []), r]);
  return [...groups].map(([reason, list]) => {
    const dated = list.filter((r) => r.at);
    const oldest = dated.at(-1) ?? null;
    const newest = dated[0] ?? null;
    return {
      reason,
      rows: list,
      from: placeName(oldest?.place ?? null),
      to: placeName(newest?.place ?? null),
      month: newest?.at ?? null,
    };
  });
}

export interface Filters {
  kind: KindFilter;
  container: string | null;
  source: string | null;
}

const keepByOthers = (r: { container: string | null; source?: string }, f: Filters) =>
  (!f.container || r.container === null || r.container === f.container) &&
  (!f.source || r.source === f.source);

export function applyFilters(rows: readonly TimelineRow[], f: Filters): TimelineRow[] {
  if (f.kind === 'withdrawn') return [];
  return rows.filter(
    (r) =>
      keepByOthers(r, f) &&
      (f.kind === 'all' || (f.kind === 'facts' ? r.kind === 'fact' : r.kind !== 'fact')),
  );
}

export function applyWithdrawnFilters(rows: readonly WithdrawnRow[], f: Filters): WithdrawnRow[] {
  if (f.kind === 'facts' || f.kind === 'forecasts') return [];
  // Withdrawn facts are the carrier's: a source other than the carrier hides them.
  if (f.source && f.source !== 'carrier') return [];
  return rows.filter((r) => !f.container || r.container === null || r.container === f.container);
}

/** The segmented control's counts, under the container and source filters. */
export function counts(
  rows: readonly TimelineRow[],
  withdrawn: readonly WithdrawnRow[],
  f: Omit<Filters, 'kind'>,
): Record<KindFilter, number> {
  const kept = rows.filter((r) => keepByOthers(r, { ...f, kind: 'all' }));
  const facts = kept.filter((r) => r.kind === 'fact').length;
  return {
    all: kept.length,
    facts,
    forecasts: kept.length - facts,
    withdrawn: applyWithdrawnFilters(withdrawn, { ...f, kind: 'withdrawn' }).length,
  };
}

const pad = (n: number) => String(n).padStart(2, '0');
export const dayKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Rows by local day, newest day first; a day after today is «forecast». Rows without a date last. */
export function groupByDay(
  rows: readonly TimelineRow[],
  now: Date,
): { days: DayGroup[]; undated: TimelineRow[] } {
  const days = new Map<string, DayGroup>();
  const undated: TimelineRow[] = [];
  const today = dayKey(now);
  for (const r of rows) {
    if (!r.at) {
      undated.push(r);
      continue;
    }
    const key = dayKey(r.at);
    let day = days.get(key);
    if (!day) {
      day = {
        key,
        date: new Date(r.at.getFullYear(), r.at.getMonth(), r.at.getDate()),
        future: key > today,
        rows: [],
      };
      days.set(key, day);
    }
    day.rows.push(r);
  }
  return { days: [...days.values()].sort((a, b) => (a.key < b.key ? 1 : -1)), undated };
}

/** A date-only value, or a time of exactly midnight on a forecast, has no time to show. */
export const showsTime = (r: Pick<TimelineRow, 'at' | 'dateOnly'>) => r.at !== null && !r.dateOnly;

// ── exports ────────────────────────────────────────────────────────────

export interface ExportRow {
  occurred_at: string | null;
  date_only: boolean;
  kind: RowKind | 'withdrawn';
  code: string;
  label: string;
  container: string | null;
  place: string | null;
  original_text: string | null;
  vessel: string | null;
  voyage: string | null;
  source: string;
  ais_confirmed: boolean;
  read_id: string | null;
  recorded_at: string | null;
  dokless_revision: number | null;
  withdrawn_reason: string | null;
}

export const EXPORT_COLUMNS: readonly (keyof ExportRow)[] = [
  'occurred_at',
  'date_only',
  'kind',
  'code',
  'label',
  'container',
  'place',
  'original_text',
  'vessel',
  'voyage',
  'source',
  'ais_confirmed',
  'read_id',
  'recorded_at',
  'dokless_revision',
  'withdrawn_reason',
];

export function exportRows(
  rows: readonly TimelineRow[],
  withdrawn: readonly WithdrawnRow[],
  label: (code: string) => string,
): ExportRow[] {
  return [
    ...rows.map((r) => ({
      occurred_at: r.atRaw ?? r.at?.toISOString() ?? null,
      date_only: r.dateOnly,
      kind: r.kind,
      code: r.code,
      label: label(r.code),
      container: r.container,
      place: r.place,
      original_text: r.originalText,
      vessel: r.vessel,
      voyage: r.voyage,
      source: r.source,
      ais_confirmed: r.ais,
      read_id: r.readId,
      recorded_at: r.readAt?.toISOString() ?? null,
      dokless_revision: r.revision,
      withdrawn_reason: null,
    })),
    ...withdrawn.map((r) => ({
      occurred_at: r.atRaw ?? r.at?.toISOString() ?? null,
      date_only: r.dateOnly,
      kind: 'withdrawn' as const,
      code: r.code,
      label: label(r.code),
      container: r.container,
      place: r.place,
      original_text: null,
      vessel: null,
      voyage: null,
      source: 'carrier',
      ais_confirmed: false,
      read_id: null,
      recorded_at: null,
      dokless_revision: null,
      withdrawn_reason: r.reason,
    })),
  ];
}

const cell = (v: unknown) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** RFC 4180 CSV with a header line (CRLF line ends). */
export function toCsv(rows: readonly ExportRow[]): string {
  const lines = [EXPORT_COLUMNS.join(',')];
  for (const r of rows) lines.push(EXPORT_COLUMNS.map((c) => cell(r[c])).join(','));
  return `${lines.join('\r\n')}\r\n`;
}
